import { type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

import { Skeleton } from './skeleton.js';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

/**
 * The mandatory empty-state template (DESIGN_SYSTEM.md §11): icon →
 * one-line headline → one sentence → one primary action. No marketing
 * copy or hero illustrations.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-border border-dashed bg-surface px-6 py-12 text-center',
        className,
      )}
    >
      <Icon className="h-10 w-10 text-fg-muted" aria-hidden />
      <div className="space-y-1">
        <p className="text-base font-semibold">{title}</p>
        {description ? (
          <p className="max-w-md text-sm text-fg-secondary">{description}</p>
        ) : null}
      </div>
      {action ? <div className="pt-2">{action}</div> : null}
    </div>
  );
}

interface ErrorStateProps {
  title?: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function ErrorState({
  title = 'Something went wrong.',
  description = 'Please try again. If the problem persists, contact your administrator.',
  action,
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-danger/30 bg-danger-subtle px-6 py-12 text-center',
        className,
      )}
    >
      <div className="space-y-1">
        <p className="text-base font-semibold text-fg">{title}</p>
        <p className="max-w-md text-sm text-fg-secondary">{description}</p>
      </div>
      {action ? <div className="pt-2">{action}</div> : null}
    </div>
  );
}

interface LoadingStateProps {
  rows?: number;
  className?: string;
}

/** Generic skeleton block used as a loading placeholder for table-like content. */
export function LoadingState({ rows = 5, className }: LoadingStateProps) {
  return (
    <div className={cn('space-y-2', className)} aria-busy="true" aria-live="polite">
      <Skeleton className="h-9 w-full" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}
